import { Divider, Input } from 'antd';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { EditIcon } from 'components/Icons';
import { JobAdjustmentParams } from 'domains/workspaceDomain';
import { CostDistributionPayload } from 'domains/orderDomain';
import RevxTextWithTooltip from 'components/RevxTextWithTooltip';
import { useOrg, useOrgById, useRole } from 'hooks';
import { CHECKOUT_ALBERTSONS_ORGS } from 'utils/constants';
import JobAdjustmentOracleBillingCode, {
  getOracleBillingCodeRows,
} from './JobAdjustmentOracleBillingCode';

type Props = {
  orderId: number;
  jobId: number;
  costCenter: string;
  accountingLine: string;
  billingContact: string;
  canShowEditButton: boolean;
  canEditStatusByRole: () => boolean;
  highlighterTitleCls: (d: string) => string;
  runDebounceUpdateJobAdjustmentCostDataHandler: (
    params: JobAdjustmentParams
  ) => void;
  generateJobAdjustmentCopy: (category?: boolean) => void;
  costDistribution?: CostDistributionPayload;
  totalCharge?: number;
  oracleBillingCode?: string;
  /** Org the job is billed to, which is what decides the billing layout. */
  customerOrgId?: number;
  // Billing contact / cost center / accounting line stay hidden from a supplier
  // that is not also the proxy, the Oracle Billing Code is shown either way.
  showBillingFields?: boolean;
  onEditOracleBillingCode?: () => void;
};

const JobAdjustmentHeader = function ({
  orderId,
  jobId,
  costCenter,
  accountingLine,
  canShowEditButton,
  canEditStatusByRole,
  highlighterTitleCls,
  runDebounceUpdateJobAdjustmentCostDataHandler,
  generateJobAdjustmentCopy,
  billingContact,
  costDistribution,
  totalCharge,
  oracleBillingCode,
  customerOrgId,
  showBillingFields = true,
  onEditOracleBillingCode,
}: Props) {
  const org = useOrg();
  const { sourcerRole, supplierRole } = useRole();
  const canEditBillingContact =
    (sourcerRole && org?.sourcerAdjustBillingContact) ||
    (supplierRole && org?.supplierAdjustBillingContact);
  const defaultText = (str: string) => {
    const width = canEditBillingContact ? 200 : 300;
    return str ? (
      <RevxTextWithTooltip text={str} maxWidth={width} />
    ) : (
      <span>None</span>
    );
  };

  // Only Albertsons orgs split the charge over a cost distribution. Everybody
  // else carries a single billing code that is edited in place. The job is
  // billed to its customer org, which is not always the org being viewed from.
  const isAlbertsonsOrg = !!(
    customerOrgId && CHECKOUT_ALBERTSONS_ORGS.includes(customerOrgId)
  );
  // Albertsons orgs carry the cost center and the accounting line inside the cost
  // distribution below, so the standalone fields would only repeat them.
  const showCostFields = !isAlbertsonsOrg;
  const hasBillingFields = canEditBillingContact || showCostFields;

  const oracleBillingCodeRows = isAlbertsonsOrg
    ? getOracleBillingCodeRows(costDistribution, totalCharge)
    : [];
  const hasOracleBillingCode = oracleBillingCodeRows.length > 0;
  // The org the job was created in decides whether it works with a billing
  // code at all, so its row is kept even while empty when the org asked for it.
  const jobOrg = useOrgById(isAlbertsonsOrg ? undefined : customerOrgId);
  const jobOrgShowsOracleBillingCode = !!jobOrg?.orderDetailPageConfig
    ?.showOracleBillingCode;
  // Otherwise there is nothing to read without a code, so the row is dropped
  // rather than reading "None". It stays while editing, that is where a code
  // is entered.
  const showInlineOracleBillingCode =
    canEditStatusByRole() ||
    !!oracleBillingCode ||
    jobOrgShowsOracleBillingCode;

  const editOracleBillingCodeHandler = () => {
    // The owner of the screen knows whether the adjustment copy has to be created
    // first, and reopens the modal on the new tab once it is.
    if (!onEditOracleBillingCode) {
      generateJobAdjustmentCopy();
      return;
    }
    onEditOracleBillingCode();
  };

  // Standalone only when it is the single row rendered, otherwise it sits inside
  // the multi row wrapper and must not repeat the job-info block styles.
  const infoRowNested = isAlbertsonsOrg
    ? hasOracleBillingCode
    : showInlineOracleBillingCode;
  const infoRow = (
    <RevxFlexLayout className={infoRowNested ? 'job-info__row' : 'job-info'}>
      {canEditBillingContact && (
        <>
          <RevxFlexLayout>
            <span className="job-info__label">Billing Contact:</span>
            {canEditStatusByRole() ? (
              <Input
                className={highlighterTitleCls('billingContact')}
                disabled={!canEditStatusByRole()}
                defaultValue={billingContact}
                onChange={async (e) => {
                  await runDebounceUpdateJobAdjustmentCostDataHandler({
                    billingContact: e.target.value.trim(),
                  });
                }}
              />
            ) : (
              <RevxFlexLayout className={highlighterTitleCls('billingContact')}>
                <span>{defaultText(billingContact)}</span>
                <div
                  className="edit-icon"
                  onClick={() => generateJobAdjustmentCopy()}
                >
                  {canShowEditButton && <EditIcon />}
                </div>
              </RevxFlexLayout>
            )}
          </RevxFlexLayout>
          {showCostFields && <Divider type="vertical" />}
        </>
      )}

      {showCostFields && (
        <>
          <RevxFlexLayout>
            <span className="job-info__label">Cost Center:</span>
            {canEditStatusByRole() ? (
              <Input
                className={highlighterTitleCls('costCenter')}
                disabled={!canEditStatusByRole()}
                defaultValue={costCenter}
                onChange={async (e) => {
                  await runDebounceUpdateJobAdjustmentCostDataHandler({
                    costCenter: e.target.value.trim(),
                  });
                }}
              />
            ) : (
              <RevxFlexLayout className={highlighterTitleCls('costCenter')}>
                <span>{defaultText(costCenter)}</span>
                <div
                  className="edit-icon"
                  onClick={() => generateJobAdjustmentCopy()}
                >
                  {canShowEditButton && <EditIcon />}
                </div>
              </RevxFlexLayout>
            )}
          </RevxFlexLayout>
          <Divider type="vertical" />
          <RevxFlexLayout>
            <span className="job-info__label">Accounting line:</span>
            {canEditStatusByRole() ? (
              <Input
                className={highlighterTitleCls('accountingLine')}
                disabled={!canEditStatusByRole()}
                defaultValue={accountingLine}
                onChange={async (e) => {
                  await runDebounceUpdateJobAdjustmentCostDataHandler({
                    accountingLine: e.target.value.trim(),
                  });
                }}
              />
            ) : (
              <RevxFlexLayout className={highlighterTitleCls('accountingLine')}>
                <span>{defaultText(accountingLine)}</span>
                <div
                  className="edit-icon"
                  onClick={() => generateJobAdjustmentCopy()}
                >
                  {canShowEditButton && <EditIcon />}
                </div>
              </RevxFlexLayout>
            )}
          </RevxFlexLayout>
        </>
      )}
    </RevxFlexLayout>
  );

  // Non Albertsons orgs edit the single billing code in place, like the other
  // fields of this header, so no modal is involved.
  const inlineOracleBillingCodeRow = (
    <RevxFlexLayout
      className={`job-info__oracle ${highlighterTitleCls('oracleBillingCode')}`}
    >
      <span className="job-info__label">Oracle Billing Code:</span>
      {canEditStatusByRole() ? (
        <Input
          className="job-info__oracle-input"
          defaultValue={oracleBillingCode}
          onChange={async (e) => {
            await runDebounceUpdateJobAdjustmentCostDataHandler({
              oracleBillingCode: e.target.value.trim(),
            });
          }}
        />
      ) : (
        <>
          <div className="job-info__oracle-value">
            <RevxTextWithTooltip text={oracleBillingCode} maxWidth="100%" />
          </div>
          <div
            className="edit-icon"
            onClick={() => generateJobAdjustmentCopy()}
          >
            {canShowEditButton && <EditIcon />}
          </div>
        </>
      )}
    </RevxFlexLayout>
  );

  const billingRow = showBillingFields && hasBillingFields ? infoRow : null;
  const wrapperCls = `job-info job-info--multi-row${
    billingRow ? '' : ' job-info--oracle-only'
  }`;

  if (!isAlbertsonsOrg) {
    if (!showInlineOracleBillingCode) return billingRow;
    return (
      <div className={wrapperCls}>
        {billingRow}
        {inlineOracleBillingCodeRow}
      </div>
    );
  }

  if (!hasOracleBillingCode) return billingRow;

  return (
    <div className={wrapperCls}>
      {billingRow}
      <JobAdjustmentOracleBillingCode
        rows={oracleBillingCodeRows}
        canShowEditButton={canShowEditButton}
        className={highlighterTitleCls('costDistribution')}
        onEdit={editOracleBillingCodeHandler}
      />
    </div>
  );
};

export default JobAdjustmentHeader;
